import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';

type ThemeMode = 'light' | 'dark';

interface ThemeContextType {
  theme: ThemeMode;
  toggleTheme: () => void;
  setTheme: (theme: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [theme, setTheme] = useState<ThemeMode>('light');

  useEffect(() => {
    // Load theme from localStorage
    const savedTheme = localStorage.getItem('totalbat-theme') as ThemeMode;
    if (savedTheme && (savedTheme === 'light' || savedTheme === 'dark')) {
      setTheme(savedTheme);
    }
  }, []);

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    localStorage.setItem('totalbat-theme', newTheme);
  };

  const handleSetTheme = (newTheme: ThemeMode) => {
    setTheme(newTheme);
    localStorage.setItem('totalbat-theme', newTheme);
  };

  // Create Material-UI theme based on current mode
  const muiTheme = createTheme({
    palette: {
      mode: theme,
      primary: {
        main: '#1E40AF',
        light: '#3B82F6',
        dark: '#1E3A8A',
      },
      secondary: {
        main: '#EA580C',
        light: '#F97316',
        dark: '#C2410C',
      },
      text: {
        primary: theme === 'light' ? '#1F2937' : '#FFFFFF',
        secondary: theme === 'light' ? '#64748B' : '#E5E7EB',
      },
      background: {
        default: theme === 'light' ? '#F8FAFC' : '#111827',
        paper: theme === 'light' ? '#FFFFFF' : '#1F2937',
      },
    },
    typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      h1: {
        fontWeight: 700,
      },
      h2: {
        fontWeight: 600,
      },
      h3: {
        fontWeight: 600,
      },
      h4: {
        fontWeight: 600,
      },
      h5: {
        fontWeight: 600,
      },
      h6: {
        fontWeight: 600,
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderRadius: 8,
            fontWeight: 600,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            boxShadow: theme === 'light' 
              ? '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)'
              : '0 1px 3px 0 rgba(0, 0, 0, 0.3), 0 1px 2px 0 rgba(0, 0, 0, 0.2)',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            boxShadow: theme === 'light' 
              ? '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)'
              : '0 1px 3px 0 rgba(0, 0, 0, 0.3), 0 1px 2px 0 rgba(0, 0, 0, 0.2)',
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: theme === 'light' ? '#FFFFFF' : '#1F2937',
            color: theme === 'light' ? '#1F2937' : '#FFFFFF',
            borderBottom: `1px solid ${theme === 'light' ? '#E5E7EB' : '#374151'}`,
            '& .MuiToolbar-root': {
              color: theme === 'light' ? '#1F2937' : '#FFFFFF',
            },
            '& .MuiIconButton-root': {
              color: theme === 'light' ? '#1F2937' : '#FFFFFF',
              '&:hover': {
                backgroundColor: theme === 'light' ? 'rgba(30, 64, 175, 0.1)' : 'rgba(255, 255, 255, 0.1)',
              },
            },
            '& .MuiButton-root': {
              color: theme === 'light' ? '#1F2937' : '#FFFFFF',
              '&:hover': {
                backgroundColor: theme === 'light' ? 'rgba(30, 64, 175, 0.1)' : 'rgba(255, 255, 255, 0.1)',
              },
            },
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            backgroundColor: theme === 'light' ? '#FFFFFF' : '#1F2937',
            color: theme === 'light' ? '#1F2937' : '#FFFFFF',
            border: `1px solid ${theme === 'light' ? '#E5E7EB' : '#374151'}`,
            boxShadow: theme === 'light' 
              ? '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
              : '0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.2)',
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            color: theme === 'light' ? '#1F2937' : '#FFFFFF',
            '&:hover': {
              backgroundColor: theme === 'light' ? '#F3F4F6' : '#374151',
            },
            '& .MuiListItemIcon-root': {
              color: theme === 'light' ? '#1F2937' : '#FFFFFF',
            },
            '& .MuiTypography-root': {
              color: theme === 'light' ? '#1F2937' : '#FFFFFF',
            },
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundColor: theme === 'light' ? '#FFFFFF' : '#1F2937',
            color: theme === 'light' ? '#1F2937' : '#FFFFFF',
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: theme === 'light' ? '#FFFFFF' : '#1F2937',
            color: theme === 'light' ? '#1F2937' : '#FFFFFF',
            borderRight: `1px solid ${theme === 'light' ? '#E5E7EB' : '#374151'}`,
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            color: theme === 'light' ? '#1F2937' : '#FFFFFF',
            '&:hover': {
              backgroundColor: theme === 'light' ? '#F3F4F6' : '#374151',
            },
            '& .MuiListItemIcon-root': {
              color: theme === 'light' ? '#1F2937' : '#FFFFFF',
            },
            '& .MuiListItemText-primary': {
              color: theme === 'light' ? '#1F2937' : '#FFFFFF',
            },
            '& .MuiListItemText-secondary': {
              color: theme === 'light' ? '#64748B' : '#E5E7EB',
            },
          },
        },
      },
      MuiListItem: {
        styleOverrides: {
          root: {
            '& .MuiListItemIcon-root': {
              color: theme === 'light' ? '#1F2937' : '#FFFFFF',
            },
            '& .MuiListItemText-primary': {
              color: theme === 'light' ? '#1F2937' : '#FFFFFF',
            },
            '& .MuiListItemText-secondary': {
              color: theme === 'light' ? '#64748B' : '#E5E7EB',
            },
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: theme === 'light' ? '#E5E7EB' : '#374151',
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          root: {
            color: theme === 'light' ? '#1F2937' : '#FFFFFF',
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            color: theme === 'light' ? '#1F2937' : '#FFFFFF',
            '&:hover': {
              backgroundColor: theme === 'light' ? 'rgba(30, 64, 175, 0.1)' : 'rgba(255, 255, 255, 0.1)',
            },
          },
        },
      },
      MuiBadge: {
        styleOverrides: {
          badge: {
            backgroundColor: theme === 'light' ? '#EA580C' : '#F97316',
            color: theme === 'light' ? '#FFFFFF' : '#000000',
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            color: theme === 'light' ? '#1F2937' : '#F9FAFB',
            '&.MuiChip-colorSuccess': {
              backgroundColor: theme === 'light' ? '#10B981' : '#059669',
              color: '#FFFFFF',
            },
            '&.MuiChip-colorWarning': {
              backgroundColor: theme === 'light' ? '#F59E0B' : '#D97706',
              color: '#FFFFFF',
            },
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            backgroundColor: theme === 'light' ? '#FFFFFF' : '#1F2937',
            color: theme === 'light' ? '#1F2937' : '#F9FAFB',
            border: `1px solid ${theme === 'light' ? '#E5E7EB' : '#374151'}`,
          },
        },
      },
    },
  });

  const value: ThemeContextType = {
    theme,
    toggleTheme,
    setTheme: handleSetTheme,
  };

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={muiTheme}>
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
}; 